import React, { useMemo } from 'react';
import { CheckCircle2, AlertTriangle, Ghost } from 'lucide-react';
import { getVisualCategoryCoverage, getTypeCategoryCoverage, type CategoryCoverageReport } from '../../content/questionBank/registryExplorer/categoryCoverage';
/**
 * Surfaces drift between the runtime registries (`VISUAL_REGISTRY`,
 * `QUESTION_TYPE_REGISTRY`) and the admin explorer's hard-coded category
 * mappings (`VISUAL_CATEGORIES`, `TYPE_CATEGORIES`).
 *
 * Implements the governance-check half of ADMIN_DOMAIN_RC_AUDIT_2026 §19.2.2 /
 * P1-2. The UI bucket "Overige Actieve…" already absorbs uncategorised items
 * in the explorer, but a designer/dev needed an at-a-glance signal that the
 * mapping is out of sync so new registry entries get properly placed.
 */
export function CategoryCoverageWidget() {
  const visualReport = useMemo(() => getVisualCategoryCoverage(), []);
  const typeReport = useMemo(() => getTypeCategoryCoverage(), []);
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-8212" data-mp="muaitvh6jlu3">
      <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-8213" data-mp="2630b6uqs88h3">
        <h3 className="text-sm font-semibold text-gray-900" data-id="element-8214" data-mp="215glmyomw99u">
          Registry-categorie dekking
        </h3>
        <p className="text-xs text-gray-500 mt-0.5" data-id="element-8215" data-mp="1wcdxx76jfu1k">
          Controleert of elk geregistreerd item in een explorer-categorie zit
        </p>
      </div>
      <div className="divide-y divide-gray-100" data-id="element-8216" data-mp="1ncqxoopjoua3">
        <CoverageRow label="Visuals" report={visualReport} data-id="element-8217" data-mp="1nelkk8kdmwgt" />
        <CoverageRow label="Vraagtypen" report={typeReport} data-id="element-8218" data-mp="1idea21qunj6g" />
      </div>
    </div>;
}
interface CoverageRowProps {
  label: string;
  report: CategoryCoverageReport;
}
function CoverageRow({
  label,
  report
}: CoverageRowProps) {
  const isClean = report.uncategorised.length === 0 && report.phantom.length === 0;
  return <div className="px-4 sm:px-5 py-4" data-id="element-8219" data-mp="37rub4fiyu3ve">
      <div className="flex items-start justify-between gap-3" data-id="element-8220" data-mp="37qrhhzu9dnbs">
        <div className="flex items-center gap-2" data-id="element-8221" data-mp="33slhrbru880d">
          {isClean ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-8222" data-mp="114v7sqfodxs8" /> : <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" data-id="element-8223" data-mp="112ijzwn8kt79" />}
          <div data-id="element-8224" data-mp="15wwi46ta9efm">
            <div className="text-sm font-medium text-gray-900" data-id="element-8225" data-mp="15xemjqq3myym">{label}</div>
            <div className="text-xs text-gray-500" data-id="element-8226" data-mp="s589wem5na3x">
              {report.categorisedCount} van {report.registrySize} geregistreerde
              items gekoppeld aan een categorie
            </div>
          </div>
        </div>
        {isClean && <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-8227" data-mp="1jt9wh72iszpv">
            In sync
          </span>}
      </div>

      {report.uncategorised.length > 0 && <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3" data-id="element-8228" data-mp="1fsq2gcd8uc7y">
          <div className="text-xs font-semibold text-amber-900 mb-1.5 flex items-center gap-1.5" data-id="element-8229" data-mp="3vk472djqplhw">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-8230" data-mp="4evpw5dmxmwz" />
            {report.uncategorised.length} ongekategoriseerd
            {report.uncategorised.length === 1 ? '' : 'e'} item
            {report.uncategorised.length === 1 ? '' : 's'}
          </div>
          <p className="text-[11px] text-amber-800 mb-2" data-id="element-8231" data-mp="3o0uy2lf5botd">
            Deze items staan wel in de registry maar zijn nog niet toegewezen
            aan een categorie in <code data-id="element-8232" data-mp="w5aocuhv6da">categoryDefinitions.ts</code>.
          </p>
          <div className="flex flex-wrap gap-1" data-id="element-8233" data-mp="uluem8u8vbi">
            {report.uncategorised.map(id => <code key={id} className="text-[10px] font-mono bg-white border border-amber-200 text-amber-800 px-1.5 py-0.5 rounded" data-id="element-8234" data-mp="39chxtc90zjt2">
            
                {id}
              </code>)}
          </div>
        </div>}

      {report.phantom.length > 0 && <div className="mt-3 bg-rose-50 border border-rose-200 rounded-lg p-3" data-id="element-8235" data-mp="3eat6vkrhxqa8">
          <div className="text-xs font-semibold text-rose-900 mb-1.5 flex items-center gap-1.5" data-id="element-8236" data-mp="2qlqsqmw2mfkh">
            <Ghost className="w-3.5 h-3.5" data-id="element-8237" data-mp="2lmnlqwwj5z9v" />
            {report.phantom.length} verweesde categorie-verwijzing
            {report.phantom.length === 1 ? '' : 'en'}
          </div>
          <p className="text-[11px] text-rose-800 mb-2" data-id="element-8238" data-mp="1ot64apb1yrh1">
            Deze category-mapping verwijst naar items die niet (meer) in de
            registry voorkomen — hernoemd of verwijderd zonder de mapping bij te
            werken.
          </p>
          <ul className="space-y-0.5" data-id="element-8239" data-mp="22mxabnpkrt80">
            {report.phantom.map(p => <li key={`${p.categoryId}:${p.itemId}`} className="text-[11px] font-mono text-rose-800" data-id="element-8240" data-mp="27lqshtft48ei">
            
                <span className="text-rose-500" data-id="element-8241" data-mp="2ckbt3tyz0u3h">{p.categoryId}</span> →{' '}
                {p.itemId}
              </li>)}
          </ul>
        </div>}
    </div>;
}